/**
 * Send Report Dialog
 *
 * Allows sending inspection reports to customers via WhatsApp or Email.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogActions,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputAdornment,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { channelColors } from '@/styles/theme';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { describeApiError } from '@/lib/describeApiError';

type DeliveryChannel = 'whatsapp' | 'email';

interface SendReportDialogProps {
  open: boolean;
  onClose: () => void;
  inspectionId: string;
  reportId: string;
  reportUrl?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerName?: string;
  vehicleRegNumber?: string;
  onSend: (params: {
    inspectionId: string;
    reportId: string;
    channel: DeliveryChannel;
    recipientPhone?: string;
    recipientEmail?: string;
    message?: string;
  }) => Promise<void>;
  isLoading?: boolean;
}

export function SendReportDialog({
  open,
  onClose,
  inspectionId,
  reportId,
  reportUrl,
  customerPhone,
  customerEmail,
  customerName,
  vehicleRegNumber,
  onSend,
  isLoading = false,
}: SendReportDialogProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const { isDirty, markDirty } = useFormDirty(open);
  const [channel, setChannel] = useState<DeliveryChannel>('whatsapp');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhone(customerPhone ?? '');
      setEmail(customerEmail ?? '');
      setMessage('');
      setError(null);
      setChannel(customerPhone ? 'whatsapp' : customerEmail ? 'email' : 'whatsapp');
    }
  }, [open, customerPhone, customerEmail]);

  const handleSend = async () => {
    setError(null);

    if (channel === 'whatsapp' && !phone.trim()) {
      setError(t('sendReport.phoneRequired'));
      return;
    }

    if (channel === 'email' && !email.trim()) {
      setError(t('sendReport.emailRequired'));
      return;
    }

    if (channel === 'email' && !email.includes('@')) {
      setError(t('sendReport.invalidEmail'));
      return;
    }

    try {
      await onSend({
        inspectionId,
        reportId,
        channel,
        recipientPhone: channel === 'whatsapp' ? phone.trim() : undefined,
        recipientEmail: channel === 'email' ? email.trim() : undefined,
        message: message.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(describeApiError(err, tc, { fallback: t('sendReport.sendFailed') }));
    }
  };

  const handlePreview = () => {
    if (reportUrl) {
      window.open(reportUrl, '_blank');
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('sendReport.title')}
      headerIcon={<SendIcon color="primary" />}
      isDirty={isDirty}
      isLoading={isLoading}
      maxWidth="sm"
      fullWidth
      renderActions={({ onClose: handleClose, isLoading: loading }) => (
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            {tc('cancel')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : channel === 'whatsapp' ? (
                <WhatsAppIcon />
              ) : (
                <EmailIcon />
              )
            }
            onClick={() => void handleSend()}
            disabled={loading}
            sx={
              channel === 'whatsapp'
                ? {
                    backgroundColor: channelColors.whatsapp,
                    '&:hover': { backgroundColor: '#128C7E' }, // design-allow-hex: official WhatsApp brand hover green (domain color)
                  }
                : {}
            }
          >
            {loading
              ? t('sendReport.sending')
              : channel === 'whatsapp'
                ? t('sendReport.sendWhatsApp')
                : t('sendReport.sendEmail')}
          </Button>
        </DialogActions>
      )}
    >
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Customer info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('sendReport.recipient')}
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {customerName || tc('customer')}
          </Typography>
          {vehicleRegNumber && (
            <Typography variant="body2" color="text.secondary">
              {vehicleRegNumber}
            </Typography>
          )}
        </Box>

        {/* Channel selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('sendReport.deliveryMethod')}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={channel}
              onChange={(e) => { setChannel(e.target.value as DeliveryChannel); markDirty(); }}
            >
              <FormControlLabel
                value="whatsapp"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ color: channelColors.whatsapp }} />
                    <Typography>WhatsApp</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="email"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography>{t('sendReport.email')}</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Recipient input */}
        <Box sx={{ mb: 3 }}>
          {channel === 'whatsapp' ? (
            <TextField
              fullWidth
              autoFocus
              label={t('sendReport.phoneNumber')}
              value={phone}
              onChange={(e) => { setPhone(e.target.value); markDirty(); }}
              placeholder={t('sendReport.phonePlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText={t('sendReport.phoneHelperText')}
            />
          ) : (
            <TextField
              fullWidth
              autoFocus
              label={t('sendReport.emailAddress')}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); markDirty(); }}
              placeholder={t('sendReport.emailPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {/* Optional message */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('sendReport.additionalMessage')}
            value={message}
            onChange={(e) => { setMessage(e.target.value); markDirty(); }}
            placeholder={t('sendReport.messagePlaceholder')}
            helperText={t('sendReport.charCount', { count: message.length, max: 500 })}
            inputProps={{ maxLength: 500 }}
          />
        </Box>

        {/* Preview button */}
        {reportUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handlePreview}
            sx={{ mb: 2 }}
          >
            {t('sendReport.previewReport')}
          </Button>
        )}
      </DialogContent>
    </FormDialog>
  );
}
